import { isValidElement, type ReactElement, type ReactNode } from "react";
import { type ColorTokens, isWeb, styled, XStack, YStack } from "tamagui";
import { khTekaFontSize, khTekaLineHeight } from "../../themes/token-fontSizes";
import { Icon, type IconComponent } from "../Icon";
import { Body1, Caption } from "../Typography";
import { FIELD_FRAME_ATTR } from "./constants";
import { FieldHelperRow } from "./FieldHelperRow";
export type FieldValidation = "none" | "error" | "success";

/** A slot is either an icon component (rendered via `<Icon>`) or arbitrary content. */
export type FieldShellSlot = IconComponent | ReactElement;
const fieldFrameVariants = {
  validation: {
    none: {
      backgroundColor: "$surface2",
      borderColor: "$borderDefault"
    },
    error: {
      backgroundColor: "$bgNegative",
      borderColor: "$borderNegative"
    },
    success: {
      backgroundColor: "$surface2",
      borderColor: "$borderDefault"
    }
  },
  disabled: {
    true: {
      backgroundColor: "$bgDisabledSubtle",
      borderColor: "$borderStrong",
      cursor: "not-allowed"
    }
  },
  focused: {
    true: {
      borderWidth: 2,
      // Compensate padding to avoid layout shift when border grows from 1px to 2px
      paddingHorizontal: 11,
      // $sp1.5 (12px) - 1px
      paddingVertical: 9 // 10px - 1px
    }
  },
  focusedValidation: {
    none: {
      borderColor: "$borderIntense"
    },
    error: {
      borderColor: "$borderNegativeStrong"
    },
    success: {
      borderColor: "$borderIntense"
    }
  },
  // Multi-line controls: top-aligned, height grows with content.
  multiline: {
    true: {
      height: "auto",
      minHeight: 56,
      alignItems: "flex-start"
    }
  }
} as const;
const FieldShellContainer = styled(YStack, {
  name: "FieldShellContainer",
  width: "100%"
});
const FieldFrame = styled(XStack, {
  name: "FieldFrame",
  height: 56,
  borderRadius: "$br2",
  borderWidth: 1,
  paddingHorizontal: "$sp1.5",
  paddingVertical: 10,
  alignItems: "center",
  gap: "$sp2",
  cursor: "text",
  style: {
    transitionProperty: "background-color, border-color",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease-out"
  },
  hoverStyle: {
    borderColor: "$borderDefaultHover"
  },
  variants: fieldFrameVariants,
  defaultVariants: {
    validation: "none"
  }
});
const FIELD_LAYOUT = {
  labelLineHeight: khTekaLineHeight.caption,
  labelInputGap: 2,
  // Resting scale: body1 / caption font size so the label looks like placeholder text
  labelRestingScale: khTekaFontSize.body1 / khTekaFontSize.caption,
  // Resting translateY: (gap + inputLineHeight) / 2 centers the label in FieldContent.
  // Works with center-origin scaling because the label container uses width: 0,
  // which pins the x-origin to the left edge; only y needs centering compensation.
  labelRestingTranslateY: (2 + khTekaLineHeight.body1) / 2
} as const;
const FieldContent = styled(YStack, {
  name: "FieldContent",
  flex: 1,
  justifyContent: "center",
  overflow: "visible",
  // prevent native from clipping the scaled label

  variants: {
    // Multi-line: pin the label to the top, let the control flow beneath it.
    multiline: {
      true: {
        justifyContent: "flex-start",
        alignSelf: "stretch"
      }
    }
  } as const
});

// Zero-width wrapper whose center-origin effectively anchors transforms
// to the left edge, avoiding transformOrigin issues with Moti on native.
const LabelContainer = styled(YStack, {
  name: "LabelContainer",
  width: 0,
  overflow: "visible",
  height: FIELD_LAYOUT.labelLineHeight,
  marginBottom: FIELD_LAYOUT.labelInputGap,
  pointerEvents: "none",
  // CSS transitions handle web; native uses Moti via animation prop on the instance
  style: {
    transitionProperty: "transform",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease-out"
  }
});
const FloatingLabel = styled(Caption, {
  name: "FloatingLabel",
  color: "$textSubtle",
  pointerEvents: "none",
  // Absolute so the text isn't constrained by the 0-width parent
  position: "absolute",
  left: 0,
  top: 0,
  whiteSpace: "nowrap",
  variants: {
    validation: {
      none: {
        color: "$textSubtle"
      },
      error: {
        color: "$textNegative"
      },
      success: {
        color: "$textSubtle"
      }
    }
  } as const,
  defaultVariants: {
    validation: "none"
  }
});
const PrefixText = styled(Body1, {
  name: "PrefixText",
  color: "$textSubtle"
});
const SuffixText = styled(Body1, {
  name: "SuffixText",
  color: "$textSubtle"
});
const renderIconSlot = (slot: FieldShellSlot | undefined, color: ColorTokens): ReactNode => {
  if (slot === undefined) {
    return null;
  }
  return isValidElement(slot) ? slot : <Icon icon={slot} size="$iconMedium" color={color} data-sentry-element="Icon" data-sentry-component="renderIconSlot" data-sentry-source-file="FieldShell.tsx" />;
};
export type FieldShellProps = {
  /** The control rendered inside the frame (text input, select trigger, etc.). */
  children: ReactNode;
  validation?: FieldValidation;
  disabled?: boolean;
  /** Owner-controlled active-focus styling (border grows 1→2px + color shift). */
  focused?: boolean;
  /** Taller, top-aligned frame for multi-line controls. */
  multiline?: boolean;
  left?: FieldShellSlot;
  right?: FieldShellSlot;
  prefix?: ReactNode;
  suffix?: ReactNode;
  /** Floating label text. `labelFloated`/`focused` are owner-controlled so a
   *  Select or Combobox can float the label when its overlay opens. */
  label?: string;
  labelFloated?: boolean;
  labelId?: string;
  /** Appends a required-marker asterisk to the floating label; it rides the
   *  same float transform as the label and shows the error color once floated. */
  required?: boolean;
  helperText?: string;
  helperTextId?: string;
  showCharacterCount?: boolean;
  currentLength?: number;
  maxLength?: number;
  /** Press handler for the frame — owners use it to focus the control. */
  onPress?: () => void;
  /** Pointer cursor for non-editable pressable triggers (Select/Combobox);
   *  editable inputs keep the default text cursor. */
  pressable?: boolean;
};
export const FieldShell = ({
  children,
  validation = "none",
  disabled = false,
  focused = false,
  multiline = false,
  left,
  right,
  prefix,
  suffix,
  label,
  labelFloated = false,
  labelId,
  required = false,
  helperText,
  helperTextId,
  showCharacterCount = false,
  currentLength = 0,
  maxLength,
  onPress,
  pressable = false
}: FieldShellProps) => {
  const iconColor: ColorTokens = disabled ? "$iconSubtle" : validation === "error" ? "$iconNegative" : "$iconDefault";
  return <FieldShellContainer data-sentry-element="FieldShellContainer" data-sentry-component="FieldShell" data-sentry-source-file="FieldShell.tsx">
      <FieldFrame validation={validation} disabled={disabled} focused={focused} focusedValidation={focused ? validation : undefined} multiline={multiline} onPress={disabled ? undefined : onPress} cursor={pressable ? "pointer" : undefined}
    // Stable anchor hook for Dropdown (owned by us, not a Tamagui class). Web-only.
    {...isWeb && {
      [FIELD_FRAME_ATTR]: ""
    }} {...(focused || disabled) && {
      hoverStyle: undefined
    }} data-sentry-element="FieldFrame" data-sentry-source-file="FieldShell.tsx">
        {renderIconSlot(left, iconColor)}

        {typeof prefix === "string" ? <PrefixText>{prefix}</PrefixText> : prefix}

        <FieldContent multiline={multiline} data-sentry-element="FieldContent" data-sentry-source-file="FieldShell.tsx">
          {label ? <LabelContainer y={labelFloated ? 0 : FIELD_LAYOUT.labelRestingTranslateY} scale={labelFloated ? 1 : FIELD_LAYOUT.labelRestingScale} {...!isWeb && {
          transition: "150ms easeOut",
          animateOnly: ["transform"]
        }}>
              <FloatingLabel validation={validation} color={!labelFloated && validation === "none" ? "$textSubtle" : undefined} id={labelId}>
                {label}
                {required ? <Caption aria-hidden
            // Subtle while the label rests inline (reads as placeholder);
            // error color once it floats on top, the standard required cue.
            color={!labelFloated && validation === "none" ? "$textSubtle" : "$textNegative"}>
                    {" "}
                    *
                  </Caption> : null}
              </FloatingLabel>
            </LabelContainer> : null}
          {children}
        </FieldContent>

        {typeof suffix === "string" ? <SuffixText>{suffix}</SuffixText> : suffix}

        {renderIconSlot(right, iconColor)}
      </FieldFrame>

      <FieldHelperRow validation={validation} helperText={helperText} helperTextId={helperTextId} showCharacterCount={showCharacterCount} currentLength={currentLength} maxLength={maxLength} data-sentry-element="FieldHelperRow" data-sentry-source-file="FieldShell.tsx" />
    </FieldShellContainer>;
};